<template>
  <vue-topprogress
    ref="topProgress"
    :color="loaderColor"
    class="top-loader"
    :class="topLoaderClasses"
  />
</template>

<script>
import { vueTopprogress } from "vue-top-progress";
import { mapGetters, mapMutations, mapState } from "vuex";
import { theme } from "twConfig";

import { componentConfig } from "@/services/_bit/UiService";

export default {
  components: {
    vueTopprogress,
  },

  props: {
    /**
     * The name of API resource (endpoint URI).
     */
    resourceNames: {
      type: [String, Array],
      default: "",
    },

    /**
     * The z-index of the loader stripe.
     * @values auto, 50
     */
    zIndex: {
      type: String,
      default: "auto",
    },

    /**
     * Loader position.
     * @values fixed, absolute, relative
     */
    position: {
      type: String,
      default: "fixed",
    },

    /**
     * The color of the loader stripe.
     * @values gray, red, orange, yellow, green, blue, violet, fuchsia
     */
    color: {
      type: String,
      default: componentConfig.ourTopLoader?.color || "blue",
    },
  },

  computed: {
    ...mapState("loaderTop", ["loaderRequestQueue", "componentLoaderRequestQueue", "isLoading"]),
    ...mapGetters({ hexColor: componentConfig.ourTopLoader?.colorGetter }),

    loaderColor() {
      const isExistHexColorGetter = componentConfig.ourTopLoader?.colorGetter;

      const color = isExistHexColorGetter ? this.hexColor : this.color;
      const colorSet = theme.colors[this.color];
      const colorNumber = 500;

      const colorHexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
      const isColorHex = colorHexRegex.test(color);

      if (!isColorHex && !colorSet) {
        return "blue";
      }

      return isColorHex ? color : colorSet[colorNumber];
    },

    resourceNamesArray() {
      return Array.isArray(this.resourceNames) ? [...this.resourceNames] : [this.resourceNames];
    },

    topLoaderClasses() {
      const zIndex = { auto: "top-loader-z-auto", 50: "top-loader-z-50" };
      const position = `top-loader-position-${this.position}`;

      return [zIndex[this.zIndex], position];
    },
  },

  watch: {
    loaderRequestQueue: {
      deep: true,
      handler: "onChangeRequestsQuare",
    },

    isLoading: {
      deep: true,
      handler: "onChangeLoadingState",
    },
  },

  created() {
    if (this.resourceNames) {
      this.SET_COMPONENT_REQUEST_QUEUE(this.resourceNamesArray);
    }
  },

  beforeDestroy() {
    if (this.resourceNames) {
      this.REMOVE_COMPONENT_REQUEST_QUEUE();
    }
  },

  methods: {
    ...mapMutations("loaderTop", ["SET_COMPONENT_REQUEST_QUEUE", "REMOVE_COMPONENT_REQUEST_QUEUE"]),

    requestWithoutQuery(request) {
      const [requestWithoutQuery] = request.split("?");

      return requestWithoutQuery;
    },

    onChangeLoadingState() {
      if (
        !this.resourceNames &&
        this.$refs.topProgress.isStarted &&
        this.$refs.topProgress.show &&
        !this.isLoading
      ) {
        this.$refs.topProgress.done();
      }
    },

    onChangeRequestsQuare() {
      let isActiveRequests = false;

      if (this.resourceNames) {
        this.resourceNamesArray.forEach((item) => {
          if (!isActiveRequests) {
            const activeRequest = this.loaderRequestQueue.find(
              (request) => this.requestWithoutQuery(request) === item
            );

            isActiveRequests = !!activeRequest;
          }
        });

        if (isActiveRequests && !this.$refs.topProgress.isStarted) {
          this.$refs.topProgress.start();
        } else if (
          !isActiveRequests &&
          this.$refs.topProgress.isStarted &&
          this.$refs.topProgress.show
        ) {
          this.$refs.topProgress.done();
        }
      } else {
        this.loaderRequestQueue.forEach((item) => {
          const activeRequest = this.componentLoaderRequestQueue.find(
            (request) => request === this.requestWithoutQuery(item)
          );

          isActiveRequests = !activeRequest;
        });

        if (this.isLoading && isActiveRequests && !this.$refs.topProgress.isStarted) {
          this.$refs.topProgress.start();
        }
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.top-loader {
  &-position {
    &-fixed {
      @apply !fixed;
    }

    &-absolute {
      @apply !absolute;
    }
  }

  &-z-auto {
    @apply !z-auto;
  }

  &-z-50 {
    @apply !z-50;
  }

  &:deep(.peg) {
    @apply !hidden;
  }
}
</style>
